import React, { useState, useEffect } from 'react';

// どこか1ヶ所（たとえば AdminDashboard の先頭）にこれだけでOK
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const gastricTests = ["胃カメラ", "バリウム", "受診無し"];
  const locations = ["西日本産業衛生会（福岡）", "西日本産業衛生会（八幡）", "西日本産業衛生会（北九州）", "博多駅東クリニック"];
  const [confirmDate, setConfirmDate] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmComment, setConfirmComment] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // コンポーネントがマウントされた時にデータを取得
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/submissions.php`);
        // 例: AdminDashboard.js
        console.log("API URL:", process.env.REACT_APP_API_BASE_URL);

        const data = await response.json();

        // ここで型チェック
        if (Array.isArray(data)) {
          setSubmissions(data);
        } else {
          console.error("想定外のレスポンス:", data);
          setSubmissions([]); // エラーや空データでも map() が落ちないように
        }
      } catch (error) {
        console.error("取得エラー:", error);
        setSubmissions([]); // fetch自体失敗した場合も配列で初期化
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  // 削除処理
  const deleteSubmission = async (id) => {
    if (!window.confirm("本当に削除しますか？")) return;
    try {
      await fetch(`${API_BASE_URL}/delete.php?id=${id}`, {
        method: "DELETE",
      });
      setSubmissions(submissions.filter(sub => sub.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (error) {
      console.error("削除エラー:", error);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedSubmissions = React.useMemo(() => {
    const sortable = [...submissions];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = a[sortConfig.key] ?? '';
        const valB = b[sortConfig.key] ?? '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [submissions, sortConfig]);

  // 編集フォームの入力変更処理
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelected(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (str) => {
    // "2025-10-01T00:00:00" → "2025/10/01"
    return str.slice(0, 10).replace(/-/g, '/');
  };

  const [isCompanyOnly, setIsCompanyOnly] = useState(false);

  useEffect(() => {
    if (selected?.location === "博多駅東クリニック") {
      setIsCompanyOnly(true);
      setSelected(prev => ({ ...prev, mailOption: "会社" }));
    } else {
      setIsCompanyOnly(false);
    }
  }, [selected?.location]);

  // 編集内容の更新処理
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/update.php?id=${selected.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selected),
      });

      const updated = await response.json();

      // 🔽 更新成功後に一覧を再取得（差分反映ではなく最新の全体に更新）
      const res = await fetch(`${API_BASE_URL}/submissions.php`);
      const latest = await res.json();
      setSubmissions(Array.isArray(latest) ? latest : []);
      setSelected(null);
    } catch (error) {
      console.error("更新エラー:", error);
    }
  };

  const handleConfirm = async () => {
    if (!confirmDate) {
      alert("診療日を選択してください");
      return;
    }

    if (!window.confirm(`${selected.name}さんの予約を確定させますか？`)) {
      return;
    }

    setConfirming(true);
    try {
      setConfirming(true);
      const res = await fetch(`${API_BASE_URL}/confirm_submission.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId: selected.id,
          confirmedDate: confirmDate,
          comment: confirmComment,
        })
      });

      const result = await res.json();
      if (result.status === 'success') {
        alert("確定通知を送信しました。");
        setConfirmDate('');
        setConfirmComment('');

        // ここで最新の一覧を再取得して画面に反映
        const res = await fetch(`${API_BASE_URL}/submissions.php`);
        const latest = await res.json();
        setSubmissions(Array.isArray(latest) ? latest : []);

        setSelected(null);
      }
      else {
        alert("送信に失敗しました: " + (result.error || '不明なエラー'));
      }
    } catch (err) {
      console.error("確定送信エラー:", err);
      alert("通信エラーが発生しました");
    } finally {
      setConfirming(false);
    }
  };


  // ローディング中の表示
  if (loading) return <div>読み込み中...</div>;

  return (
    <div style={{ display: 'flex', gap: '40px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: '6px' }}>
      {/* 表側 */}
      <div style={{ flex: 2 }}>
        <h1 style={{ textAlign: 'center', marginBottom: '20px' }}>健康診断アンケート - 管理者画面</h1>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f0f2f5' }}>
              <th style={thStyle} onClick={() => handleSort('confirmedDate')}>
                確定日 {sortConfig.key === 'confirmedDate' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('name')}>
                名前 {sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('employeeId')}>
                社員番号 {sortConfig.key === 'employeeId' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('location')}>
                受診場所 {sortConfig.key === 'location' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('mailOption')}>
                問診票送付先 {sortConfig.key === 'mailOption' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('firstChoice')}>
                第一希望 {sortConfig.key === 'firstChoice' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('secondChoice')}>
                第二希望 {sortConfig.key === 'secondChoice' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle} onClick={() => handleSort('gastricTest')}>
                胃部検査 {sortConfig.key === 'gastricTest' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th style={thStyle}>追加希望検査</th>
              <th style={thStyle}>編集</th>
              <th style={thStyle}>削除</th>
            </tr>
          </thead>
          <tbody>
            {sortedSubmissions.map((s) => {
              const isEditing = selected?.id === s.id;
              return (
                <tr
                  key={s.id}
                  style={{
                    textAlign: 'center',
                    borderBottom: '1px solid #ddd',
                    backgroundColor: isEditing ? '#e6f7ff' : 'transparent', // 👈 ここで編集中の行を水色に
                  }}
                >
                  <td style={tdStyle}>
                    {s.confirmedDate ? formatDate(s.confirmedDate) : '未確定'}
                  </td>
                  <td style={tdStyle}>
                    {s.name}
                    {isEditing && (
                      <span style={{ marginLeft: '6px', color: '#1890ff', fontWeight: 'bold' }}>
                        編集中
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>{s.employeeId}</td>
                  <td style={tdStyle}>{s.location}</td>
                  <td style={tdStyle}>{s.mailOption}</td>
                  <td style={tdStyle}>{formatDate(s.firstChoice)}</td>
                  <td style={tdStyle}>{formatDate(s.secondChoice)}</td>
                  <td style={tdStyle}>{s.gastricTest}</td>
                  <td style={tdStyle}>
                    <textarea
                      value={s.additionalTests}
                      readOnly
                      style={{
                        width: '100%',
                        maxHeight: '5em',
                        overflowY: 'auto',
                        resize: 'none',
                        border: 'none',
                        background: 'transparent',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: '1.4',
                        color: '#333',
                      }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => setSelected(s)}
                      style={editBtnStyle}
                      onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                      onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      編集
                    </button>
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => deleteSubmission(s.id)}
                      style={deleteBtnStyle}
                      onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                      onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      削除
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 編集フォーム */}
      {selected && (
        <div style={{
          flex: 1,
          borderLeft: '1px solid #ccc',
          paddingLeft: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '6px'
        }}>
          <form onSubmit={handleEditSubmit}>
            <h2 style={{ marginBottom: '20px' }}>編集フォーム</h2>

            {/* 名前（固定表示） */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>名前</label>
              <p style={readOnlyFieldStyle}>{selected.name}</p>
            </div>

            {/* 社員番号（固定表示） */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>社員番号</label>
              <p style={readOnlyFieldStyle}>{selected.employeeId}</p>
            </div>

            {/* 受診場所 */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>受診場所</label>
              <select
                name="location"
                value={selected.location}
                onChange={handleChange}
                style={inputStyle}
              >
                {locations.map((loc, i) => (
                  <option key={i} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            {/* 問診票送付先（ラジオボタン） */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>問診票送付先</label>
              {selected.location === "博多駅東クリニック" ? (
                <>
                  <p style={{ fontWeight: 'bold', color: '#555' }}>※「博多駅東クリニック」のため「会社」に固定</p>
                  <p style={readOnlyFieldStyle}>会社</p>
                </>
              ) : (
                <div style={{ display: 'flex', gap: '30px', marginTop: '5px' }}>
                  {["自宅", "会社"].map(option => (
                    <label key={option} style={{ whiteSpace: 'nowrap' }}>
                      <input
                        type="radio"
                        name="mailOption"
                        value={option}
                        checked={selected.mailOption === option}
                        onChange={handleChange}
                        style={{ marginRight: '5px' }}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 郵便番号（固定表示） */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>郵便番号</label>
              <p style={readOnlyFieldStyle}>{selected.postalCode || '未設定'}</p>
            </div>

            {/* 住所（固定表示） */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>住所</label>
              <p style={readOnlyFieldStyle}>{selected.address || '未設定'}</p>
            </div>

            {/* 第一希望日時 */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>第一希望日時</label>
              <input
                name="firstChoice"
                type="date"
                value={selected.firstChoice.slice(0, 10)}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            {/* 第二希望日時 */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>第二希望日時</label>
              <input
                name="secondChoice"
                type="date"
                value={selected.secondChoice.slice(0, 10)}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            {/* 胃部検査 */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>胃部検査</label>
              <select
                name="gastricTest"
                value={selected.gastricTest}
                onChange={handleChange}
                style={inputStyle}
              >
                {gastricTests.map((test, i) => (
                  <option key={i} value={test}>{test}</option>
                ))}
              </select>
            </div>

            {/* 追加希望検査 */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>追加希望検査</label>
              <input
                name="additionalTests"
                value={selected.additionalTests || ''}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            {/* 確定選択（第一 or 第二） */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>診療日を確定</label>
              <div style={{ display: 'flex', gap: '20px' }}>
                <label>
                  <input
                    type="radio"
                    name="confirmDate"
                    value={selected.firstChoice.slice(0, 10)}
                    checked={confirmDate === selected.firstChoice.slice(0, 10)}
                    onChange={(e) => setConfirmDate(e.target.value)}
                  />
                  第一希望（{formatDate(selected.firstChoice)}）
                </label>
                <label>
                  <input
                    type="radio"
                    name="confirmDate"
                    value={selected.secondChoice.slice(0, 10)}
                    checked={confirmDate === selected.secondChoice.slice(0, 10)}
                    onChange={(e) => setConfirmDate(e.target.value)}
                  />
                  第二希望（{formatDate(selected.secondChoice)}）
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>管理者コメント（任意）</label>
              <textarea
                value={confirmComment}
                onChange={(e) => setConfirmComment(e.target.value)}
                placeholder="伝達事項あればここに書いて確定"
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {/* 確定ボタン */}
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={confirming || !confirmDate}
                style={{
                  backgroundColor: '#722ed1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  padding: '10px 20px',
                  fontSize: '16px',
                  cursor: confirming ? 'not-allowed' : 'pointer',
                  opacity: confirming ? 0.6 : 1,
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {confirming ? '送信中...' : 'この内容で確定通知'}
              </button>
            </div>

            {/* ボタン */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '0' }}>
              <button
                type="submit"
                style={submitBtnStyle}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                更新
              </button>
              <button
                type="button"
                onClick={() => setSelected(null)}
                style={cancelBtnStyle}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// テーブルのスタイル
const thStyle = {
  padding: '10px',
  borderBottom: '2px solid #ccc',
  textAlign: 'center',
  cursor: 'pointer',
};

const tdStyle = {
  padding: '10px',
  whiteSpace: 'nowrap',
};

const inputStyle = {
  width: '100%',
  padding: '8px',
  border: '1px solid #ccc',
  borderRadius: '4px',
};

const editBtnStyle = {
  backgroundColor: '#1890ff',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: '6px 12px',
  cursor: 'pointer',
  transition: 'transform 0.2s',
};

const deleteBtnStyle = {
  backgroundColor: '#f5222d',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: '6px 12px',
  cursor: 'pointer',
  transition: 'transform 0.2s',
};

const submitBtnStyle = {
  backgroundColor: '#52c41a',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  padding: '10px 20px',
  fontSize: '16px',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  minWidth: '100px',
};

const cancelBtnStyle = {
  backgroundColor: '#f5222d',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  padding: '10px 20px',
  fontSize: '16px',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  minWidth: '100px',
};

const readOnlyFieldStyle = {
  color: '#333',
  backgroundColor: '#f5f5f5',
  padding: '8px 12px',
  borderRadius: '4px',
  fontSize: '16px',
  fontWeight: '500',
  border: '1px solid #ddd',
  userSelect: 'none'
};

export default AdminDashboard;
